<template>
  <v-card>
    <v-card-title class="primary white--text">
      <span class="headline">{{ $t('eudrSendTitle', { label: report.label || $t('untitled') }) }}</span>
      <v-spacer/>
      <Icon
        icon="mdi-close"
        iconColor="white"
        dataTestId="eudr-send-close"
        :tooltipText="$t('close')"
        :small="false"
        :large="true"
        @icon-clicked="$emit('close')"
      />
    </v-card-title>

    <v-card-text class="pt-4">
      <p class="caption grey--text text--darken-1 mb-2">{{ $t('eudrSendInfo') }}</p>

      <v-radio-group v-model="artifact" dense hide-details class="mt-0 mb-3">
        <v-radio :label="$t('eudrSendArtifactShare')" value="share"/>
        <v-radio :label="$t('eudrSendArtifactSubmission')" value="sanitized"/>
      </v-radio-group>

      <v-progress-linear v-if="loading" indeterminate color="primary"/>

      <template v-else>
        <v-alert v-if="!candidates.length" type="info" dense text class="caption mb-0">
          {{ $t('eudrSendNoCandidates') }}
        </v-alert>

        <div v-else class="eudr-send-list">
          <div
            v-for="candidate in candidates"
            :key="candidate.accountId"
            class="d-flex align-center px-2 py-1"
          >
            <v-simple-checkbox
              :value="selectedIds.includes(candidate.accountId)"
              color="primary"
              :ripple="false"
              @click="toggle(candidate)"
            />
            <div class="flex-grow-1">
              <div class="body-2">{{ addNameWithCode(candidate.name, candidate.code) }}</div>
            </div>
          </div>
        </div>
      </template>
    </v-card-text>

    <v-card-actions>
      <v-spacer/>
      <v-btn text @click="$emit('close')">{{ $t('cancel') }}</v-btn>
      <v-btn
        color="primary"
        :loading="sending"
        :disabled="loading || sending || !selectedIds.length"
        @click="send"
      >
        <v-icon left>mdi-email-fast-outline</v-icon>
        {{ $t('eudrSendConfirm', { count: selectedIds.length }) }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex'
import { buildShareFile } from '@/utils/eudr/EudrExport.js'
import { eudrClient } from '@/utils/EudrClient.js'

export default {
  name: 'EudrSendDialog',

  components: {
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    // A Finalized composite batch row from the manager grid.
    report: { type: Object, required: true }
  },

  data: () => ({
    loading: true,
    sending: false,
    candidates: [],
    selectedIds: [],
    artifact: 'share'
  }),

  async created () {
    try {
      this.candidates = await this.fetchShareCandidates(this.report.eudrBatchId)
    } catch (e) {
    } finally {
      this.loading = false
    }
  },

  methods: {
    ...mapActions('eudr', ['fetchShareCandidates', 'fetchWriteSas', 'sendEudrShareFile']),

    toggle (candidate) {
      const index = this.selectedIds.indexOf(candidate.accountId)
      if (index === -1) this.selectedIds.push(candidate.accountId)
      else this.selectedIds.splice(index, 1)
    },

    // The emailed link is minted by the notification agent, not here.
    async send () {
      if (!this.selectedIds.length) return
      this.sending = true
      try {
        // The sanitized submission file already exists from finalize; only the share flavor is built on demand.
        if (this.artifact === 'share') {
          const blob = await eudrClient.fetchReportBlob(this.report.validatedBlobPath || this.report.resultBlobPath)
          const parsed = JSON.parse(await blob.text())
          const excluded = new Set((parsed.features ?? [])
            .filter(f => f.properties?.excluded === true)
            .map(f => f.properties.featureId))
          const share = buildShareFile(parsed, excluded)

          const sas = await this.fetchWriteSas({ id: this.report.eudrBatchId, artifact: 'share' })
          await eudrClient.uploadBlobWithSas(sas.sasUri, JSON.stringify(share))
        }

        const result = await this.sendEudrShareFile({ id: this.report.eudrBatchId, artifact: this.artifact, accountIds: this.selectedIds })
        this.setSnack(this.$t('eudrSendQueued', { count: result.queuedEmailCount }))
        if (result.skippedAccounts?.length) {
          this.setSnackError(this.$t('eudrSendSkipped', { accounts: result.skippedAccounts.join(', ') }))
        }
        this.$emit('sent')
      } catch (e) {
        this.setSnackError(this.$t('eudrSendFailed'))
      } finally {
        this.sending = false
      }
    }
  }
}
</script>

<style scoped>
.eudr-send-list {
  max-height: 50vh;
  overflow-y: auto;
}
</style>
